import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { io } from "socket.io-client";
import axios from "../api/axiosConfig";
import { Send, Store, User, Bike, Lock, RefreshCw, ArrowLeft, MessageSquare } from "lucide-react";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

/* ── Design tokens ───────────────────────────────────────────────────────── */
const T = {
  bg:      "#0d1117",
  surface: "#161b22",
  border:  "rgba(255,255,255,0.07)",
  divider: "rgba(255,255,255,0.06)",
  text:    "#e6edf3",
  muted:   "#8b949e",
  store:   "#3b82f6",
  ryder:   "#8b5cf6",
  yellow:  "#fec400",
};

/* ── Role metadata ───────────────────────────────────────────────────────── */
const ROLE_META = {
  user:    { label: "Cliente",      color: T.yellow, icon: User  },
  company: { label: "Tienda",       color: T.store,  icon: Store },
  ryder:   { label: "Domiciliario", color: T.ryder,  icon: Bike  },
};

/* ── Tab config — each tab shows a "conversation" filtered by role ────────── */
// - "store": show messages from user + company (hide ryder-only msgs)
// - "ryder": show messages from user + ryder   (hide company-only msgs)
const TABS = {
  store: {
    key: "store", label: "Tienda", icon: Store, color: T.store,
    roles: ["user", "company"],                   // visible in this tab
    visibleFor: ["user", "company"],              // which user roles see this tab
  },
  ryder: {
    key: "ryder", label: "Domiciliario", icon: Bike, color: T.ryder,
    roles: ["user", "ryder"],
    visibleFor: ["user", "ryder"],
  },
};

function isOrderUpdate(msg) {
  return typeof msg.message === "string" && msg.message.startsWith("🔄 *La tienda");
}

function fmtTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

/* ── Visual Viewport hook (iOS keyboard) ─────────────────────────────────── */
function useKeyboardOffset(active) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!active || !window.visualViewport) return;
    const update = () => {
      const gap = window.innerHeight - (window.visualViewport.height + window.visualViewport.offsetTop);
      setOffset(Math.max(0, gap));
    };
    update();
    window.visualViewport.addEventListener("resize", update);
    window.visualViewport.addEventListener("scroll", update);
    return () => {
      window.visualViewport.removeEventListener("resize", update);
      window.visualViewport.removeEventListener("scroll", update);
      setOffset(0);
    };
  }, [active]);
  return offset;
}

/* ── TypingDots ──────────────────────────────────────────────────────────── */
function TypingDots() {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 5, height: 5, borderRadius: "50%", background: T.muted,
          animation: `ocBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

/* ── OrderUpdateMessage ──────────────────────────────────────────────────── */
function OrderUpdateMessage({ msg }) {
  const body  = msg.message.replace("🔄 *La tienda actualizo tu pedido:*\n", "");
  const lines = body.split("\n").filter(Boolean);
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "8px 0", padding: "0 8px" }}>
      <div style={{ width: "100%", maxWidth: "92%", borderRadius: 12, overflow: "hidden", background: "rgba(254,196,0,0.06)", border: "1px dashed rgba(254,196,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderBottom: "1px dashed rgba(254,196,0,0.2)", background: "rgba(254,196,0,0.08)" }}>
          <RefreshCw size={10} style={{ color: T.yellow }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: T.yellow, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace" }}>Pedido modificado</span>
        </div>
        <div style={{ padding: "8px 12px" }}>
          {lines.map((l, i) => <p key={i} style={{ fontSize: 12, color: "#cbd5e1", margin: "2px 0" }}>{l}</p>)}
        </div>
        <p style={{ padding: "0 12px 7px", fontSize: 10, textAlign: "right", color: T.muted, fontFamily: "monospace" }}>{fmtTime(msg.createdAt)}</p>
      </div>
    </div>
  );
}

/* ── Message Bubble ──────────────────────────────────────────────────────── */
function Bubble({ msg, userRole }) {
  const role   = msg.senderRole ?? msg.sender?.role ?? "user";
  const isMine = role === userRole;
  const meta   = ROLE_META[role] ?? ROLE_META.user;
  const Icon   = meta.icon;

  if (isOrderUpdate(msg)) return <OrderUpdateMessage msg={msg} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", damping: 24, stiffness: 340 }}
      style={{ display: "flex", width: "100%", gap: 8, justifyContent: isMine ? "flex-end" : "flex-start" }}
    >
      {!isMine && (
        <div style={{ width: 28, height: 28, borderRadius: 9, flexShrink: 0, alignSelf: "flex-end", marginBottom: 2, display: "flex", alignItems: "center", justifyContent: "center", background: meta.color + "1a", border: `1px solid ${meta.color}33` }}>
          <Icon size={12} style={{ color: meta.color }} />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", maxWidth: "76%", gap: 3, alignItems: isMine ? "flex-end" : "flex-start" }}>
        {!isMine && (
          <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, letterSpacing: "0.08em", textTransform: "uppercase", marginLeft: 4 }}>
            {meta.label}
          </span>
        )}
        <div style={{
          padding: "9px 13px",
          borderRadius: isMine ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
          background: isMine ? "linear-gradient(135deg,#fec400,#ffe566)" : "rgba(255,255,255,0.07)",
          color: isMine ? "#0d1117" : T.text,
          border: isMine ? "none" : `1px solid ${T.border}`,
          boxShadow: isMine ? "0 4px 16px rgba(254,196,0,0.22)" : "none",
          wordBreak: "break-word", overflowWrap: "anywhere",
        }}>
          <p style={{ fontSize: 14, lineHeight: 1.5, fontWeight: isMine ? 600 : 400, whiteSpace: "pre-wrap", margin: 0 }}>
            {msg.message}
          </p>
          <p style={{ fontSize: 10, textAlign: "right", marginTop: 4, fontFamily: "monospace", color: isMine ? "rgba(13,17,23,0.5)" : T.muted }}>
            {fmtTime(msg.createdAt)}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────────────── */
function EmptyState({ tab }) {
  const cfg  = TABS[tab]; const Icon = cfg.icon;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24 }}>
      <div style={{ width: 50, height: 50, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: cfg.color + "15", border: `1px solid ${cfg.color}25` }}>
        <Icon size={20} style={{ color: cfg.color }} />
      </div>
      <p style={{ fontSize: 12, textAlign: "center", color: T.muted, lineHeight: 1.6, margin: 0 }}>
        {tab === "ryder" ? "Cuando el domiciliario\nsea asignado podras\ncomunicarte aqui" : "Inicia la conversacion\ncon la tienda"}
      </p>
    </div>
  );
}

/* ── Tab button ──────────────────────────────────────────────────────────── */
function TabBtn({ cfg, active, unread, onClick }) {
  const Icon = cfg.icon;
  return (
    <button onClick={onClick} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 10px", borderRadius: 9, border: "none", cursor: "pointer", background: active ? cfg.color + "18" : "transparent", outline: active ? `1px solid ${cfg.color}33` : "none", transition: "all 0.18s", position: "relative" }}>
      <Icon size={13} style={{ color: active ? cfg.color : T.muted, transition: "color 0.18s" }} />
      <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", color: active ? cfg.color : T.muted, transition: "color 0.18s" }}>
        {cfg.label}
      </span>
      {unread > 0 && (
        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
          style={{ position: "absolute", top: 4, right: 6, minWidth: 16, height: 16, borderRadius: 8, background: cfg.color, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>
          {unread > 9 ? "9+" : unread}
        </motion.span>
      )}
    </button>
  );
}

/* ── Main export ─────────────────────────────────────────────────────────── */
export default function OrderChat({ orderId, kind = "store", user, isActive, onNewMessage }) {
  const userRole = user?.role ?? "user";
  const defaultTab = userRole === "ryder" ? "ryder" : "store";

  /* Determine which tabs are visible for this role */
  const visibleTabs = Object.values(TABS).filter(t => t.visibleFor.includes(userRole));

  const [tab,      setTab]      = useState(defaultTab);
  const [mobileFS, setMobileFS] = useState(false);
  const [text,     setText]     = useState("");
  const [sending,  setSending]  = useState(false);

  /* Chat state — single room shared by all tabs */
  const [chatId,   setChatId]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [typing,   setTyping]   = useState([]);  // [{role, tab}] currently typing per tab

  /* Unread counters per tab (reset when tab is focused) */
  const [unread, setUnread] = useState({ store: 0, ryder: 0 });

  const socketRef    = useRef(null);
  const typingTimers = useRef({});
  const stopTimer    = useRef(null);
  const textareaRef  = useRef(null);
  const endRef       = useRef(null);
  const tabRef       = useRef(tab);
  const onMsgRef     = useRef(onNewMessage);
  const kbWasOpen    = useRef(false);
  tabRef.current     = tab;
  onMsgRef.current   = onNewMessage;

  const kbOffset = useKeyboardOffset(mobileFS);

  /* Load chat */
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    axios.get(`/chats/order/${orderId}`, { params: { kind } })
      .then(async res => {
        const chat = res.data?.data?.chat ?? res.data?.data ?? res.data;
        if (!chat?.id) return;
        setChatId(chat.id);
        const msgRes = await axios.get(`/chats/${chat.id}/messages`);
        const list   = msgRes.data?.data?.data ?? msgRes.data?.data ?? [];
        setMessages(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId, kind]);

  /* Socket.io — single connection for the room */
  useEffect(() => {
    if (!chatId) return;
    const token  = localStorage.getItem("accessToken") || localStorage.getItem("token");
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"], withCredentials: true, auth: { token } });
    socketRef.current = socket;
    socket.on("connect", () => socket.emit("chat:join", chatId));

    socket.on("message:new", msg => {
      setMessages(prev => {
        const safe = Array.isArray(prev) ? prev : [];
        return safe.some(m => m.id === msg.id) ? safe : [...safe, msg];
      });
      const role = msg.senderRole ?? msg.sender?.role;
      if (role && role !== userRole) {
        /* Determine which tab this message belongs to */
        const msgTab = role === "ryder" ? "ryder" : "store";
        if (msgTab !== tabRef.current) {
          setUnread(u => ({ ...u, [msgTab]: u[msgTab] + 1 }));
          onMsgRef.current?.();
        }
        /* Clear typing for that role */
        clearTimeout(typingTimers.current[role]);
        setTyping(prev => prev.filter(r => r !== role));
      }
    });

    socket.on("chat:typing", ({ role }) => {
      if (role === userRole) return;
      setTyping(prev => prev.includes(role) ? prev : [...prev, role]);
      clearTimeout(typingTimers.current[role]);
      typingTimers.current[role] = setTimeout(() => setTyping(prev => prev.filter(r => r !== role)), 4000);
    });

    socket.on("chat:stop-typing", ({ role }) => {
      clearTimeout(typingTimers.current[role]);
      setTyping(prev => prev.filter(r => r !== role));
    });

    return () => {
      socket.disconnect();
      Object.values(typingTimers.current).forEach(clearTimeout);
      clearTimeout(stopTimer.current);
    };
  }, [chatId, userRole]);

  /* Clear unread on tab switch */
  const switchTab = useCallback(nextTab => {
    setTab(nextTab);
    setUnread(u => ({ ...u, [nextTab]: 0 }));
  }, []);

  /* Scroll to bottom */
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, tab, mobileFS]);

  /* Mobile fullscreen */
  const lockBody   = useCallback(() => { document.body.style.overflow = "hidden"; document.documentElement.style.overflow = "hidden"; }, []);
  const unlockBody = useCallback(() => { document.body.style.overflow = ""; document.documentElement.style.overflow = ""; }, []);
  useEffect(() => () => unlockBody(), [unlockBody]);

  const openFS  = useCallback(() => { if (window.innerWidth >= 768) return; lockBody(); setMobileFS(true); setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 400); }, [lockBody]);
  const closeFS = useCallback(() => { setMobileFS(false); unlockBody(); textareaRef.current?.blur(); kbWasOpen.current = false; }, [unlockBody]);

  useEffect(() => {
    if (!mobileFS) { kbWasOpen.current = false; return; }
    if (kbOffset > 0) { kbWasOpen.current = true; return; }
    if (!kbWasOpen.current) return;
    const t = setTimeout(() => { if (document.activeElement !== textareaRef.current) closeFS(); }, 300);
    return () => clearTimeout(t);
  }, [kbOffset, mobileFS, closeFS]);

  /* Emit typing */
  const emitTyping = () => {
    socketRef.current?.emit("chat:typing", chatId);
    clearTimeout(stopTimer.current);
    stopTimer.current = setTimeout(() => socketRef.current?.emit("chat:stop-typing", chatId), 2500);
  };

  /* Send message */
  const handleSend = async () => {
    if (!text.trim() || !chatId || !isActive || sending) return;
    const msg = text.trim(); setText("");
    textareaRef.current?.focus();
    clearTimeout(stopTimer.current);
    socketRef.current?.emit("chat:stop-typing", chatId);
    setSending(true);
    try { await axios.post(`/chats/${chatId}/messages`, { message: msg }); }
    catch { setText(msg); }
    finally { setSending(false); }
  };

  const handleKeyDown = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  /* Filter messages for active tab */
  const tabCfg     = TABS[tab] ?? TABS.store;
  const tabRoles   = tabCfg.roles;
  const filteredMsgs = messages.filter(m => {
    const role = m.senderRole ?? m.sender?.role ?? "user";
    return tabRoles.includes(role);
  });

  /* Typing roles relevant to current tab */
  const tabTyping = typing.filter(r => tabRoles.includes(r) && r !== userRole);
  const typingLabel = tabTyping.length > 0
    ? (ROLE_META[tabTyping[0]]?.label ?? "Alguien") + " esta escribiendo"
    : null;

  /* Does the ryder tab have any ryder participant yet? */
  const ryderHasMessages = messages.some(m => (m.senderRole ?? m.sender?.role) === "ryder");
  const showRyderEmpty   = tab === "ryder" && !loading && !ryderHasMessages;

  /* ── Shell ── */
  const shell = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: T.bg, borderRadius: mobileFS ? 0 : "inherit", paddingTop: mobileFS ? "env(safe-area-inset-top,0px)" : 0 }}>

      <style>{`
        @keyframes ocBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
        @keyframes ocPulse  { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>

      {/* Brand stripe */}
      <div style={{ height: 3, background: "linear-gradient(90deg,#e29800,#fec400,#fff246,#fec400,#e29800)", flexShrink: 0 }} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${T.divider}`, flexShrink: 0 }}>
        {mobileFS && (
          <button onClick={closeFS} style={{ width: 32, height: 32, borderRadius: 10, border: "none", background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <ArrowLeft size={15} style={{ color: T.text }} />
          </button>
        )}
        <div style={{ width: 30, height: 30, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(254,196,0,0.1)", border: "1px solid rgba(254,196,0,0.2)", flexShrink: 0 }}>
          <MessageSquare size={13} style={{ color: T.yellow }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.text, textTransform: "uppercase", letterSpacing: "0.12em", fontFamily: "monospace" }}>Chat del pedido</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          {isActive
            ? <><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px #4ade80", display: "inline-block", animation: "ocPulse 2s ease-in-out infinite" }} /><span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", textTransform: "uppercase", letterSpacing: "0.1em" }}>En vivo</span></>
            : <><Lock size={10} style={{ color: T.muted }} /><span style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Cerrado</span></>
          }
        </div>
      </div>

      {/* Tabs — only when role sees multiple tabs */}
      {visibleTabs.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: "8px 10px", borderBottom: `1px solid ${T.divider}`, flexShrink: 0, background: T.surface }}>
          {visibleTabs.map(cfg => (
            <TabBtn key={cfg.key} cfg={cfg} active={tab === cfg.key} unread={unread[cfg.key]} onClick={() => switchTab(cfg.key)} />
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 10px 6px", minHeight: 0, display: "flex", flexDirection: "column", gap: 6, scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.08) transparent", overscrollBehavior: "contain" }}>
        {loading && (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: T.yellow, opacity: 0.6, animation: `ocBounce 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
            </div>
          </div>
        )}

        {showRyderEmpty && <EmptyState tab="ryder" />}

        {!loading && !showRyderEmpty && filteredMsgs.length === 0 && (
          <EmptyState tab={tab} />
        )}

        <AnimatePresence initial={false}>
          {filteredMsgs.map((m, i) => (
            <Bubble key={m.id ?? i} msg={m} userRole={userRole} />
          ))}
        </AnimatePresence>
        <div ref={endRef} />
      </div>

      {/* Typing */}
      <AnimatePresence>
        {typingLabel && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.15 }}
            style={{ padding: "6px 14px", display: "flex", alignItems: "center", gap: 8, borderTop: `1px solid ${T.divider}`, flexShrink: 0 }}>
            <TypingDots />
            <span style={{ fontSize: 11, color: T.muted, fontStyle: "italic" }}>{typingLabel}...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      {!isActive ? (
        <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, borderTop: `1px solid ${T.divider}`, color: T.muted, fontSize: 12, flexShrink: 0 }}>
          <Lock size={11} /> Chat cerrado — pedido finalizado
        </div>
      ) : (
        <div style={{
          padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-end", flexShrink: 0,
          borderTop: `1px solid ${T.divider}`,
          paddingBottom: mobileFS ? `calc(10px + ${kbOffset}px + env(safe-area-inset-bottom,0px))` : 10,
          transition: "padding-bottom 0.15s ease",
        }}>
          <textarea
            ref={textareaRef}
            rows={1} value={text}
            onChange={e => { setText(e.target.value); if (e.target.value.trim()) emitTyping(); }}
            onKeyDown={handleKeyDown}
            onFocus={e => { e.target.style.borderColor = tabCfg.color + "66"; e.target.style.boxShadow = `0 0 0 3px ${tabCfg.color}18`; openFS(); }}
            onBlur={e  => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; }}
            placeholder={tab === "ryder" ? "Mensaje al domiciliario..." : "Mensaje a la tienda..."}
            style={{ flex: 1, resize: "none", fontSize: 14, maxHeight: 96, overflowY: "auto", background: "rgba(255,255,255,0.05)", border: `1px solid ${T.border}`, borderRadius: 13, padding: "9px 13px", color: T.text, transition: "border-color 0.2s, box-shadow 0.2s", lineHeight: 1.5, outline: "none", fontFamily: "inherit" }}
          />
          <button
            disabled={sending || !text.trim()}
            onClick={handleSend}
            style={{ width: 40, height: 40, borderRadius: 13, border: "none", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: (sending || !text.trim()) ? "not-allowed" : "pointer", background: (sending || !text.trim()) ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg,#fec400,#ffe566)", boxShadow: text.trim() ? "0 4px 16px rgba(254,196,0,0.28)" : "none", transform: text.trim() ? "scale(1)" : "scale(0.94)", transition: "all 0.18s ease" }}
          >
            <Send size={15} style={{ color: text.trim() ? "#0d1117" : T.muted }} />
          </button>
        </div>
      )}
    </div>
  );

  if (mobileFS) {
    return (
      <AnimatePresence>
        <motion.div key="ocFS" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", stiffness: 380, damping: 36 }}
          style={{ position: "fixed", inset: 0, zIndex: 10001, background: T.bg }}>
          {shell}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div style={{ height: 440, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)" }}>
      {shell}
    </div>
  );
}